import {
  Color,
  EquirectangularReflectionMapping,
  Group,
  LinearToneMapping,
  Mesh,
  NoToneMapping,
  Object3D,
  WebGLRenderer,
} from 'three';
import { GLTF, GLTFLoaderPlugin, GLTFParser } from 'three/addons/loaders/GLTFLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';

import { NavMesh } from '@spazious/dollhouse-navmesh';

export const EXTENSION_NAME = 'SPZ_dollhouse';

export interface SPZDollhouseDef {
  environment: {
    source: number;
  };
  background: {
    source: number;
    blurriness?: number;
    intensity?: number;
    color?: number[];
    environment?: boolean;
  };
  toneMapping: {
    type: string;
    exposure?: number;
  };
}

export const SPZToneMappingMap = {
  linear: LinearToneMapping,
};

export class SPZDollhouse implements GLTFLoaderPlugin {
  name: string;
  parser: GLTFParser;
  renderer?: WebGLRenderer;
  rgbeLoader: RGBELoader;

  constructor(parser: GLTFParser) {
    this.name = EXTENSION_NAME;
    this.parser = parser;
    this.rgbeLoader = new RGBELoader();
  }

  private async _loadEnvironment({ source }: SPZDollhouseDef['environment']) {
    const dataTexture = await this._loadSource(source);

    dataTexture.mapping = EquirectangularReflectionMapping;

    return dataTexture;
  }

  private async _loadSource(source: number) {
    return this.parser.loadImageSource(source, this.rgbeLoader);
  }

  /**
   * remap navmesh node to object.navMesh property and delete the original child mesh from scene
   */
  private _setupNavMesh(scene: Group) {
    const nodesToRemove: Object3D[] = [];

    // collect navmesh meshes
    const meshes: Mesh[] = [];
    scene.traverse((node) => {
      if (node.name === 'navmesh') {
        node.traverse((mesh) => {
          if (mesh instanceof Mesh) {
            meshes.push(mesh);
          }
        });
        nodesToRemove.push(node);
      }
    });

    if (meshes.length) {
      scene.userData.navMesh = new NavMesh(meshes);
    }
    // remove original navmesh nodes from scene
    nodesToRemove.forEach((node) => {
      node.parent?.remove(node);
    });
  }

  afterRoot(result: GLTF): Promise<void> | null {
    const parser = this.parser;
    const json = parser.json;

    return new Promise(async (resolve) => {
      const jsonScene = json.scenes?.[0];
      const ext: SPZDollhouseDef = jsonScene?.extensions?.[this.name];

      if (!ext) {
        return resolve();
      }

      // Not needed for now
      // if (!this.renderer) {
      //   throw new Error('SPZ_dollhouse: renderer must be set.');
      // }

      const scene = result.scene as any as Group;

      this._setupNavMesh(scene);

      if (ext.environment) {
        const environmentMap = await this._loadEnvironment(ext.environment);
        scene.userData.environmentMap = environmentMap;
      }

      if (ext.background) {
        const source =
          (ext.background.environment ? scene.userData.environment : ext.background.source) ?? null;
        const background = typeof source === 'number' ? await this._loadSource(source) : source;

        scene.userData.background =
          background ?? (ext.background.color ? new Color().fromArray(ext.background.color) : null);
        scene.userData.backgroundIntensity = ext.background.intensity ?? 1;
        scene.userData.backgroundBlurriness = ext.background.blurriness ?? 0;
      }

      if (ext.toneMapping) {
        scene.userData.toneMapping =
          (SPZToneMappingMap as any)[ext.toneMapping.type] ?? NoToneMapping;
        scene.userData.toneMappingExposure = ext.toneMapping.exposure ?? 1;
      }

      result.scene.traverse((node) => {
        if ((node as Mesh).isMesh) {
          const mesh = node as Mesh;
          (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).forEach((material) => {
            if (material) {
              // TODO(https://github.com/mrdoob/three.js/pull/18235): Clean up.
              material.depthWrite = !material.transparent;
            }
          });
        }
      });

      resolve();
    });
  }

  createNodeMesh(nodeIndex: number) {
    const json = this.parser.json;
    const nodeDef = json.nodes[nodeIndex];
    // Hide the dollhouse scene fcarpets and navmesh nodes
    if (/^fcarpet|navmesh/.test(nodeDef.name)) {
      return this.parser.createNodeMesh(nodeIndex).then((mesh) => {
        mesh.visible = false;
        return mesh;
      });
    }

    return null;
  }

  setRenderer(renderer: WebGLRenderer): this {
    this.renderer = renderer;
    return this;
  }
}

export default SPZDollhouse;
